/**
 * Äänestys and Valitut vipupisteet in Ongelmakartta v3's facilitator panel (#1326,
 * specs/workshop-modules/systemic_map.md v3, "Äänestys"): open the vote with how many votes a device
 * has, close it, and the live tally of the shown vipupisteet, most votes first. The facilitator
 * ticks the ones to work on, the tally only a guide, and saves them: Valitut vipupisteet, the
 * module's output for the next steps. Counts only, never who voted.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation';
import useContentRefresh from '../../../hooks/useContentRefresh';
import { v2Json } from '../v3Fetch';

const Row = styled.label`
  display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center;
  min-height: 48px; box-sizing: border-box; padding: 8px 10px; border-radius: 8px; border: 1px solid #2c3444;
  font-size: 14px; cursor: pointer;
  input { width: 20px; height: 20px; }
  small { display: block; margin-top: 2px; color: rgba(255,255,255,0.55); font-size: 12px; }
  b { font-variant-numeric: tabular-nums; font-weight: 600; }
`;
const Line = styled.div`display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin: 8px 0;`;
const Btn = styled.button`
  min-height: 44px; padding: 0 14px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; color: #e2e8f0;
  border: 1px solid ${(p) => (p.$primary ? 'transparent' : 'rgba(148,163,184,0.4)')}; background: ${(p) => (p.$primary ? '#5B9DD9' : 'transparent')};
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;
const Select = styled.select`min-height: 44px; padding: 0 8px; border-radius: 8px; background: #111827; color: #e2e8f0; border: 1px solid rgba(148,163,184,0.4);`;
const Muted = styled.p`color: rgba(255,255,255,0.55); font-size: 13px;`;

const send = (url, method, body) => v2Json(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

/** @param {{ instanceId: string, onError: (message: string) => void }} props */
export default function VotePanel({ instanceId, onError }) {
  const { t } = useTranslation();
  const k = (key, vars) => t(`ongelmakarttaV3.admin.vote.${key}`, vars);
  const [vote, setVote] = useState(null);
  const [maxVotes, setMaxVotes] = useState(3);
  const [chosen, setChosen] = useState(null); // the ticks before saving; null: the saved selection
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setVote(await v2Json(API_ENDPOINTS.SYSTEMIC_MAP_V3_VOTES(instanceId)));
    } catch (err) {
      onError(t('ongelmakarttaV3.admin.loadFailed', { error: err.message }));
    }
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useContentRefresh(instanceId, ['systemic_map_v3_votes'], load, { minIntervalMs: 2000 });

  const act = async (url, method, body) => {
    setBusy(true);
    try {
      setVote(await send(url, method, { instanceId, ...body }));
      return true;
    } catch (err) {
      onError(t('ongelmakarttaV3.admin.failed', { error: err.message }));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!vote) return null;
  if (!vote.runId) return <Muted>{k('noRun')}</Muted>;
  const votesOf = new Map((vote.tally || []).map((x) => [x.pivotId, x.votes]));
  const ranked = [...vote.pivots].sort((a, b) => (votesOf.get(b.id) || 0) - (votesOf.get(a.id) || 0));
  const ticked = new Set(chosen ?? vote.selection ?? []);
  const toggle = (id) => setChosen(() => {
    const next = new Set(ticked);
    if (next.has(id)) next.delete(id); else next.add(id);
    return [...next];
  });
  const save = async () => { if (await act(API_ENDPOINTS.SYSTEMIC_MAP_V3_SELECTION(), 'PUT', { pivotIds: [...ticked] })) setChosen(null); };

  return (
    <>
      <Muted>{k(`status.${vote.status}`, { max: vote.maxVotes, voters: vote.voters ?? 0 })}</Muted>
      <Line>
        {vote.status !== 'open' ? (
          <>
            <Select aria-label={k('maxVotes')} value={maxVotes} onChange={(e) => setMaxVotes(Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{k('votesEach', { count: n })}</option>)}
            </Select>
            <Btn type="button" $primary disabled={busy || !vote.pivots.length} onClick={() => act(API_ENDPOINTS.SYSTEMIC_MAP_V3_VOTES_OPEN(), 'POST', { maxVotes })}>
              {vote.status === 'closed' ? k('reopen') : k('open')}
            </Btn>
          </>
        ) : (
          <Btn type="button" disabled={busy} onClick={() => act(API_ENDPOINTS.SYSTEMIC_MAP_V3_VOTES_CLOSE(), 'POST', {})}>{k('close')}</Btn>
        )}
      </Line>
      <Muted>{k('chooseLead')}</Muted>
      {ranked.map((p) => (
        <Row key={p.id}>
          <input type="checkbox" checked={ticked.has(p.id)} disabled={busy} onChange={() => toggle(p.id)} />
          <span>{p.title}<small>{t(`ongelmakarttaV3.pivot.types.${p.type}`)}</small></span>
          <b>{k('votes', { count: votesOf.get(p.id) || 0 })}</b>
        </Row>
      ))}
      <Line>
        <Btn type="button" $primary disabled={busy || !ticked.size || chosen === null} onClick={save}>{k('save', { count: ticked.size })}</Btn>
        {vote.selection ? <Muted>{k('saved', { count: vote.selection.length })}</Muted> : <Muted>{k('noneSaved')}</Muted>}
      </Line>
    </>
  );
}
